import { zodResolver } from '@hookform/resolvers/zod';
import { api } from '@packages/backend/convex/_generated/api';
import {
  FEATURE_FLAG_KEYS,
  type FeatureFlagKey,
  getUserErrorMessage,
  restaurantForms,
} from '@packages/shared';
import { createFileRoute } from '@tanstack/react-router';
import { useConvex } from 'convex/react';
import { useQuery } from 'convex-helpers/react';
import { useEffect } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { toast } from 'sonner';
import type { z } from 'zod';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { ActionButton } from '@/components/ui+/ActionButton';
import { FormWrapper } from '@/components/ui+/FormWrapper';
import { LoadingIcon } from '@/components/ui+/LoadingIcon';
import { handleSubmitErrors } from '@/utils/handleSubmitErrors';

const formValuesSchema = restaurantForms.featureFlags;
type FormValues = z.output<typeof formValuesSchema>;

export const Route = createFileRoute('/r/$restaurantId/s/feature-flags')({
  component: Component,
});

function Component() {
  const convex = useConvex();
  const { restaurantId } = Route.useParams();
  const { data: restaurant, isPending } = useQuery(api.restaurants.getById, { restaurantId });

  const form = useForm({
    defaultValues: { activeFeatureFlags: [] },
    resolver: zodResolver(formValuesSchema),
  });
  const activeFeatureFlags = useWatch({ control: form.control, name: 'activeFeatureFlags' }) ?? [];

  // Subscribe to changes
  useEffect(() => {
    if (!restaurant) return;
    // Ensure un-saved user changes are kept on Convex sync
    form.reset({ activeFeatureFlags: restaurant.activeFeatureFlags ?? [] } satisfies FormValues, {
      keepDirty: true,
      keepDirtyValues: true,
    });
  }, [restaurant]);

  const setFeatureFlag = (flag: FeatureFlagKey, enabled: boolean) => {
    const currentFlags = form.getValues('activeFeatureFlags') ?? [];
    const nextFlags = enabled
      ? [...new Set([...currentFlags, flag])]
      : currentFlags.filter((currentFlag) => currentFlag !== flag);

    form.setValue('activeFeatureFlags', nextFlags, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  const onValidSubmit = async (formValues: FormValues) => {
    try {
      await convex.mutation(api.restaurants.saveFeatureFlags, {
        formValues,
        restaurantId,
      });
      // The saved values are the new baseline, so nothing is dirty any more
      form.reset(form.getValues());
      toast.success('Saved successfully');
    } catch (error) {
      const description = getUserErrorMessage(error);
      toast.error('Error saving!', { description });
    }
  };

  if (isPending) return <LoadingIcon />;
  if (!restaurant) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Feature flags</CardTitle>
        <CardDescription>Turn on restaurant-specific features.</CardDescription>
      </CardHeader>
      <CardContent>
        <FormWrapper {...form}>
          <form
            id="feature-flags-form"
            onSubmit={form.handleSubmit(onValidSubmit, handleSubmitErrors)}
            className="grid gap-4"
          >
            {FEATURE_FLAG_KEYS.map((flag) => (
              <div
                key={flag}
                className="flex items-center justify-between gap-4 rounded-lg border p-4"
              >
                <label className="font-medium" htmlFor={`feature-flag-${flag}`}>
                  {flag}
                </label>
                <Switch
                  id={`feature-flag-${flag}`}
                  checked={activeFeatureFlags.includes(flag)}
                  onCheckedChange={(enabled) => setFeatureFlag(flag, enabled)}
                />
              </div>
            ))}
          </form>
        </FormWrapper>
      </CardContent>
      <CardFooter className="border-t">
        <ActionButton
          disabled={form.formState.isSubmitting || !form.formState.isDirty}
          loading={form.formState.isSubmitting}
          type="submit"
          form="feature-flags-form"
        >
          Save
        </ActionButton>
      </CardFooter>
    </Card>
  );
}
